import React, { FC, useCallback, useMemo } from 'react';

import { ICombo, IComboSlotOption } from '@rbi-ctg/menu';
import { NUM_COLUMNS } from 'components/list-card-group/styled';
import { ProductCard } from 'components/product-card';
import { ComboSlotOptionsGrid } from 'components/product-customization/static-layout';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { isItem } from 'utils/menu';

import { transformItemToProductListItemProps } from '../utils';

import { IProductCustomizationItemOptionsProps } from './types';
import { getRowIndex, getSelectedRowIndex } from './utils';

type ComboSlotOptionWithPrice = IComboSlotOption & { price?: number };

export const ProductCustomizationItemOptions: FC<IProductCustomizationItemOptionsProps> = ({
  comboSlot,
  comboSlotOptions,
  selectedItem,
  onSelectComboSlot,
  inlineItemCustomization,
  showInlineCustomization = true,
}) => {
  const { priceForComboSlotSelection } = useMenuContext();
  const { selectedProduct } = useProductWizardContext();
  const { formatCalories } = useMenuCalories();

  const [selectedComboSlotRow, setSelectedComboSlotRow] = React.useState<{
    comboSlotOption: ComboSlotOptionWithPrice;
    rowIndex: number;
  } | null>(null);

  React.useEffect(() => {
    if (comboSlotOptions.length > 0 && selectedItem) {
      const selectedRowIndex = getSelectedRowIndex({ selectedItem, comboSlotOptions });
      const selectedComboSlotOption =
        selectedRowIndex === -1 ? comboSlotOptions[0] : comboSlotOptions[selectedRowIndex];

      setSelectedComboSlotRow({
        comboSlotOption: selectedComboSlotOption,
        rowIndex: getRowIndex(selectedRowIndex, NUM_COLUMNS),
      });
    }
  }, [selectedItem, comboSlotOptions]);

  const comboSlotOptionsWithPrice = useMemo<ComboSlotOptionWithPrice[]>(() => {
    return comboSlotOptions.map(option => {
      const upsellPrice = comboSlot
        ? priceForComboSlotSelection({
            combo: selectedProduct as ICombo,
            comboSlot,
            selectedOption: option,
          })
        : 0;
      return {
        ...option,
        price: upsellPrice,
      };
    });
  }, [comboSlot, comboSlotOptions, priceForComboSlotSelection, selectedProduct]);

  const handleSelectComboSlot = useCallback(
    (comboSlotOption: IComboSlotOption, rowIndex: number) => {
      const item = comboSlotOption.option;

      if (!isItem(item)) {
        return;
      }

      if (item.options.length) {
        setSelectedComboSlotRow({ comboSlotOption, rowIndex });
      } else {
        setSelectedComboSlotRow(null);
      }

      onSelectComboSlot(comboSlotOption);
    },
    [onSelectComboSlot]
  );

  const selectedItemCustomization = useMemo(() => {
    const hasAvailableOptions = !!selectedComboSlotRow?.comboSlotOption.option.options.length;

    return selectedComboSlotRow && hasAvailableOptions ? inlineItemCustomization : null;
  }, [inlineItemCustomization, selectedComboSlotRow]);

  return (
    <ComboSlotOptionsGrid
      items={comboSlotOptionsWithPrice}
      numColumns={NUM_COLUMNS}
      selectedRow={showInlineCustomization ? selectedComboSlotRow?.rowIndex : undefined}
      inlineItemCustomization={selectedItemCustomization}
      renderItem={({ item: comboSlotOption, rowIndex }) => {
        const productListItemProps = transformItemToProductListItemProps(comboSlotOption.option);
        const isSelected = selectedItem._id === comboSlotOption.option._id;

        return (
          <ProductCard
            key={comboSlotOption.option._id}
            isSelected={isSelected}
            onProductPress={() => handleSelectComboSlot(comboSlotOption, rowIndex)}
            accessibilityRole="radio"
            accessibilitySelected={isSelected}
            productProps={{
              ...productListItemProps.productProps,
              price: comboSlotOption.price,
              calories: formatCalories(comboSlotOption.option.nutrition?.calories),
            }}
          />
        );
      }}
    />
  );
};
